// The capture station's Day follows the work day (UF-08).
//
// THE DEFECT. The station's Day was `useState(sastTodayYMD())`, read once when
// the page opened. A tablet left open overnight kept crediting YESTERDAY all
// the next morning, and a reload part-way through a night shift (update
// banner, a Chrome 86 tab discard) moved the rest of that shift onto the next
// calendar day. Piece pay and its tiers are worked out per work_date, so one
// night could pay as two half-days.
//
// THE RULE (Jason, 22 Sep, for BigScan stations): the work day turns over at
// 06:00 SAST. A scan before 06:00 credits the day the shift started, so one
// night shift stays one pay day. BigScan's bridge does this with
// `day_rollover_hour` (tools/scan-bridge/lib/parse.js `shiftDayStr`); in the
// app the same rule is `shiftDayOf` with DEFAULT_SHIFT_CUTOFF_HOUR, which the
// register's Shift view already uses — so this file never re-derives a SAST
// day by hand.
//
// THE STATE MACHINE. The Day is either FOLLOWING the work day (nothing
// chosen) or PINNED to a day the supervisor picked on purpose. A pinned day is
// never moved by the clock — back-dating stays a deliberate act — and picking
// the current work day (or "Switch to today") goes back to following.
import { useEffect, useState } from 'react'
import { sastTodayYMD } from '@/lib/dates'
import { DEFAULT_SHIFT_CUTOFF_HOUR, shiftDayOf } from './shiftPunnets'

/** How often an open station re-reads the clock. A minute is the most a
 *  scan can be late in turning over, and costs nothing. */
export const STATION_DAY_TICK_MS = 60_000

/** The station's work day at an instant: the SAST date, minus the 06:00
 *  turnover. 05:59 on the 2nd is still the 1st; 06:00 is the 2nd. */
export function stationDayAt(nowMs: number, cutoffHour: number = DEFAULT_SHIFT_CUTOFF_HOUR): string {
  return shiftDayOf(new Date(nowMs).toISOString(), cutoffHour)
}

/** The Day in force: the one picked on purpose, else the work day. */
export function effectiveWorkDate(pinned: string | null, today: string): string {
  return pinned ?? today
}

/** What a pick from the Day field stores. The current work day (or a
 *  cleared field) means "follow the clock again"; any other day is pinned. */
export function pinFromPick(picked: string, today: string): string | null {
  return !picked || picked === today ? null : picked
}

/** The station's two dates. `day` is the work day (06:00 turnover) the Day
 *  follows; `calendarDay` is the SAST calendar date — the Day field's max, so
 *  a morning crew before 06:00 can still pick today on purpose. Both turn over
 *  on the same tick: a max read once at mount kept refusing the new calendar
 *  day all night on a tablet left open past midnight. */
export interface StationClock { day: string; calendarDay: string }

function readClock(): StationClock {
  return { day: stationDayAt(Date.now()), calendarDay: sastTodayYMD() }
}

/**
 * The station's clock, kept current while the page stays open: re-read on a
 * one-minute timer and whenever the tablet comes back to the foreground (a
 * sleeping tablet's timers do not run). A tick that changes neither date keeps
 * the previous object, so it re-renders nothing.
 */
export function useStationClock(): StationClock {
  const [clock, setClock] = useState(readClock)
  useEffect(() => {
    const reread = () => setClock(prev => {
      const next = readClock()
      return next.day === prev.day && next.calendarDay === prev.calendarDay ? prev : next
    })
    const onVisible = () => { if (document.visibilityState === 'visible') reread() }
    const timer = window.setInterval(reread, STATION_DAY_TICK_MS)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
  return clock
}
